import React, { useMemo } from 'react';
import { t } from 'i18next';
import moment from 'moment';
import { HiOutlineEye, HiDotsVertical, HiCheck } from 'react-icons/hi';
import { RadixDropdown, RadixSpinner, RadixTable } from '@/components/radix';
import type { ColumnType, RowType } from '@/components/radix';
import { cnMerge } from '@/utils/cnMerge';
import type { GetApiDoctorsPortalMessages200MessagesItem } from '@/shared/api/models';
import { getPriorityStyles, getPriorityLabel } from './messagesViewUtils';

export interface TableLayoutProps {
    messages: GetApiDoctorsPortalMessages200MessagesItem[];
    selectedDoctorId: string;
    openPreview: (msg: GetApiDoctorsPortalMessages200MessagesItem) => void;
    handleMarkSingleRead: (id: string) => void;
    markReadMutation: {
        mutate: (ids: string[]) => void;
        isPending: boolean;
        variables?: string[];
    };
    /** Current API sortBy (e.g. isPinned, title). Used for server sorting. */
    sortBy?: string;
    /** Current sort order from API. */
    sortOrder?: 'asc' | 'desc';
    /** Called when user clicks a sortable column header; columnId is table column id. */
    onSort?: (columnId: string) => void;
}

/** Map API sortBy to table column id for RadixTable defaultOrder. */
function apiSortByToColumnId(sortBy?: string): string {
    if (!sortBy) return 'title';
    return sortBy === 'isPinned' ? 'pinned' : sortBy;
}

export default function TableLayout({
    messages,
    selectedDoctorId,
    openPreview,
    handleMarkSingleRead,
    markReadMutation,
    sortBy,
    sortOrder = 'desc',
    onSort,
}: TableLayoutProps) {
    const DOCTOR_ALL_VALUE = '__all__';

    const columns = useMemo<ColumnType[]>(
        () => [
            {
                id: 'read',
                name: '',
                width: '40px',
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    if (msg.readAt) return <span className="w-4" />;
                    return (
                        <span
                            className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 shrink-0"
                            aria-hidden
                        />
                    );
                },
            },
            {
                id: 'title',
                name: t('Doctors.MessageTitle'),
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    return (
                        <p className="text-text-primary font-medium m-0 truncate max-w-[200px]">{msg.title}</p>
                    );
                },
                sortable: true,
                sortValue: (row: RowType) => (row as GetApiDoctorsPortalMessages200MessagesItem).title ?? '',
            },
            ...(selectedDoctorId === DOCTOR_ALL_VALUE
                ? [
                      {
                          id: 'doctor',
                          name: t('Common.Name'),
                          selector: (row: RowType) => {
                              const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                              if (!msg.doctor) return <span className="text-text-secondary">–</span>;
                              return (
                                  <span className="text-text-primary text-sm">
                                      {msg.doctor.firstName} {msg.doctor.lastName}
                                  </span>
                              );
                          },
                      } as ColumnType,
                  ]
                : []),
            {
                id: 'priority',
                name: t('Doctors.Priority'),
                sortable: true,
                sortValue: (row: RowType) =>
                    getPriorityLabel((row as GetApiDoctorsPortalMessages200MessagesItem).priority),
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    const styles = getPriorityStyles(msg.priority);
                    return (
                        <span
                            className={cnMerge(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                styles.badge,
                            )}
                        >
                            {getPriorityLabel(msg.priority)}
                        </span>
                    );
                },
            },
            {
                id: 'pinned',
                name: t('Doctors.Pinned'),
                width: '80px',
                sortable: true,
                sortValue: (row: RowType) =>
                    (row as GetApiDoctorsPortalMessages200MessagesItem).isPinned ? '1' : '0',
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    if (!msg.isPinned) return <span className="text-text-secondary">–</span>;
                    return (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-200">
                            {t('Doctors.Pinned')}
                        </span>
                    );
                },
            },
            {
                id: 'validUntil',
                name: t('Doctors.ValidUntil'),
                sortable: true,
                sortValue: (row: RowType) =>
                    (row as GetApiDoctorsPortalMessages200MessagesItem).validUntil ?? '',
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    if (!msg.validUntil) return <span className="text-text-secondary">–</span>;
                    return (
                        <span className="text-text-secondary text-sm">
                            {moment(msg.validUntil).format('DD/MM/YYYY HH:mm')}
                        </span>
                    );
                },
            },
            {
                id: 'readAt',
                name: t('Doctors.ReadAt'),
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    if (!msg.readAt) return <span className="text-text-secondary">–</span>;
                    return (
                        <span className="text-text-secondary text-sm">
                            {moment(msg.readAt).format('DD/MM/YYYY HH:mm')}
                        </span>
                    );
                },
            },
            {
                id: 'actions',
                name: '',
                width: '56px',
                selector: (row: RowType) => {
                    const msg = row as GetApiDoctorsPortalMessages200MessagesItem;
                    const isMarkingThis =
                        markReadMutation.isPending &&
                        Array.isArray(markReadMutation.variables) &&
                        markReadMutation.variables.length === 1 &&
                        markReadMutation.variables[0] === msg.id;
                    return (
                        <RadixDropdown
                            trigger={
                                <button
                                    type="button"
                                    className="bg-transparent p-1 focus:outline-none focus:ring-0 text-text-secondary border-none cursor-pointer rounded-sm"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <HiDotsVertical size={18} />
                                </button>
                            }
                            items={[
                                {
                                    label: (
                                        <span className="flex items-center gap-2">
                                            <HiOutlineEye className="w-4 h-4" />
                                            {t('Doctors.ViewMessage')}
                                        </span>
                                    ),
                                    onClick: () => openPreview(msg),
                                },
                                !msg.readAt
                                    ? {
                                          label: isMarkingThis ? (
                                              <span className="flex items-center gap-2">
                                                  <RadixSpinner size="sm" variant="primary" />
                                                  {t('Doctors.MarkingMessagesRead')}
                                              </span>
                                          ) : (
                                              <span className="flex items-center gap-2">
                                                  <HiCheck className="w-4 h-4" />
                                                  {t('Doctors.MarkMessageRead')}
                                              </span>
                                          ),
                                          onClick: () => handleMarkSingleRead(msg.id),
                                          disabled: markReadMutation.isPending,
                                      }
                                    : undefined,
                            ].filter(Boolean) as any}
                            align="end"
                        />
                    );
                },
            },
        ],
        [
            selectedDoctorId,
            openPreview,
            handleMarkSingleRead,
            markReadMutation.isPending,
            markReadMutation.variables,
        ],
    );

    return (
        <RadixTable
            columns={columns}
            data={messages as RowType[]}
            loading={false}
            isServerSorting={!!onSort}
            onSort={onSort}
            defaultOrder={apiSortByToColumnId(sortBy)}
            serverSortOrder={sortOrder}
        />
    );
}
